/**
 * Brand + contact info — single source of truth.
 *
 * Reads `VITE_*` env vars at build time and derives the variants we need
 * (display phone, `tel:` href, `wa.me/` path). Components import from here
 * instead of hardcoding emails / phone numbers — that way changing the
 * razón social, RUC or support number is a one-line edit in `.env.production`
 * (or, on the Hetzner box, in `.env.production.local` without a commit).
 *
 * Fallbacks are real values, NOT placeholders, so the site never renders an
 * empty href if the env file is missing.
 */

/** Pull a `VITE_*` env var with a default. */
function envOr(key: string, fallback: string): string {
  const v = import.meta.env[key as keyof ImportMetaEnv] as string | undefined;
  return v && v.trim() ? v.trim() : fallback;
}

const RAW_PHONE = envOr('VITE_SUPPORT_PHONE', '+51 955 374 097');
/** Only digits — used to build the `tel:` and `wa.me/` URLs. */
const PHONE_DIGITS = RAW_PHONE.replace(/\D/g, '');

export const contact = {
  /** Display brand name (used in alt text, copy). */
  brandName: envOr('VITE_BRAND_NAME', 'AlphaConcreto'),
  /** Legal entity name (razón social) as shown in Terms §1 / Privacy §1. */
  legalName: envOr('VITE_LEGAL_NAME', 'AlphaConcreto / Alpha+'),
  /** RUC (Peruvian tax ID). Empty string when not yet registered. */
  legalRuc: envOr('VITE_LEGAL_RUC', ''),
  /** Human-readable address line. */
  legalAddress: envOr('VITE_LEGAL_ADDRESS', 'Lima, Perú'),

  /** Generic support inbox. */
  supportEmail: envOr('VITE_SUPPORT_EMAIL', 'hola@alphaconcreto.com'),
  /** Inbox for Ley 29733 ARCO requests. */
  privacyEmail: envOr('VITE_PRIVACY_EMAIL', 'privacidad@alphaconcreto.com'),
  /** Inbox for refund requests (Culqi reversals). */
  refundsEmail: envOr('VITE_REFUNDS_EMAIL', 'reembolsos@alphaconcreto.com'),

  /** Pretty-printed phone, e.g. "+51 955 374 097". */
  phoneDisplay: RAW_PHONE,
  /** `tel:` href — leading `+` plus digits only. */
  phoneTel: `+${PHONE_DIGITS}`,
  /** `wa.me/` path — digits only, no `+`. */
  phoneWa: PHONE_DIGITS,

  /** Operating-hours string, shown below contact cards / in the footer. */
  supportHours: envOr('VITE_SUPPORT_HOURS', 'Lun–Vie 9:00–18:00 (GMT-5)'),
} as const;

export type ContactInfo = typeof contact;
